// Unpublished Work © 2023-2024 Deere & Company.

import axios from "../api";
import {
  DealerAccount,
  DealerAccountLocations,
  DealerCreditDetails,
} from "./types";

export interface Account {
  id: string;
  name: string;
  location: Location | undefined;
  isPrimaryAccount: boolean;
  mruSalesOrg: string;
}

interface Location {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phoneNumber: string;
}

class DealerApi {
  async accountMappedToUser(
    serviceURL: string,
    userXid: string,
    dealerAccountId: string,
    accessToken: string,
    isClientUI = true
  ): Promise<boolean> {
    return axios
      .getWithRetries(
        `${serviceURL}/api/v1/dealer/accounts/mapping?xid=${userXid}&isClientUI=${isClientUI}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => response.data)
      .then(
        (accounts: Account[]) =>
          !!accounts &&
          accounts.some((account) => account.id === dealerAccountId)
      )
      .catch((error) => {
        const {
          response: { status },
        } = error;
        return status === 404 || status === 400
          ? Promise.resolve(false)
          : Promise.reject(error);
      });
  }

  async accountsLoader(
    serviceURL: string,
    userXid: string,
    accessToken: string,
    isClientUI = true
  ): Promise<{ data: Account[] }> {
    return axios.getWithRetries(
      `${serviceURL}/api/v1/dealer/accounts/mapping?xid=${userXid}&isClientUI=${isClientUI}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  }

  async getAccountsDetails(
    url: string,
    accountIds: string[],
    accessToken: string,
    language: string
  ): Promise<DealerAccount[]> {
    return axios
      .getWithRetries<DealerAccount[]>(
        `${url}/api/v1/dealer/accounts?accountIds=${accountIds.join(
          ","
        )}&lang=${language}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then(({ data }) => data);
  }

  getDealerAccountLocation = async (
    dealerServiceUrl: string,
    accountIds: string[],
    accessToken: string,
    lang?: string
  ): Promise<DealerAccountLocations> => {
    const dealerAccountLocations = await axios
      .getWithRetries(
        `${dealerServiceUrl}/api/v1/dealer/accounts/location?accountIds=${accountIds}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { lang },
        }
      )
      .then((response: any) => response.data);

    return { dealerAccountLocations };
  };

  getDealerCreditDetails = async (
    dealerServiceUrl: string,
    accountId: string,
    accessToken: string,
    lang?: string
  ): Promise<DealerCreditDetails> => {
    const dealerCreditDetails = await axios
      .getWithRetries(
        `${dealerServiceUrl}/api/v1/dealer/creditDetails?accountId=${accountId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { lang },
        }
      )
      .then((response: any) => response.data);

    return { availableCreditLimit: dealerCreditDetails.availableCreditLimit };
  };
}

export default new DealerApi();
